// Shared AI Troubleshooting i18n namespace (owned by sub-plan 14). Sub-plans 18/19/20 EXTEND
// this namespace with their own distinct key subtrees; they do not re-author this file.
export const aiTroubleshooting = {
  title: 'AI Troubleshooting',
  pageTitle: 'AI Troubleshooting',
  subtitle: 'Describe a symptom and let the read-only diagnostic AI investigate.',
  menu: {
    investigate: 'Investigate',
  },
  // Menu leaf label for the playbooks child (sub-plan 18). Extends this shared namespace
  // rather than adding a separate top-level menu.aiPlaybooks key (REF-08 s.5.5).
  playbooks: 'Playbooks',
  form: {
    title: 'New investigation',
    symptom: 'Symptom',
    symptomPlaceholder:
      'Describe what is happening (e.g. charger CP123 shows Available in the app but a session is still active).',
    anchorType: 'Related entity',
    anchorId: 'Entity ID',
    anchorIdHelper: 'Paste the identifier from the relevant screen.',
    anchorIdHelperNone: 'Select a related entity type to attach an ID.',
    anchorIdRequired: 'An ID is required for the selected entity type.',
    run: 'Run investigation',
  },
  anchorType: {
    none: 'None',
    charger: 'Charger',
    session: 'Session',
    user: 'User',
    transaction: 'Transaction',
    reservation: 'Reservation',
  },
  stream: {
    title: 'Investigation steps',
    investigating: 'Investigating...',
    waiting: 'Starting the investigation...',
    stop: 'Stop',
  },
  report: {
    title: 'Report',
    refusedTitle: 'Request declined',
    refusedGeneric: 'This request is outside the scope of charging-system troubleshooting.',
    noConclusion: 'No conclusion was produced.',
    evidence: 'Evidence',
    model: 'Model',
    tokens: 'Tokens (in / out)',
    cost: 'Cost',
    latency: 'Latency',
  },
  refusedReason: {
    off_topic: 'This request is not about the charging system.',
    coding_request: 'This assistant does not write or generate code.',
    general_knowledge: 'This is a general-knowledge question, not a charging-system issue.',
    jailbreak_attempt: 'This request attempts to override the assistant instructions.',
    out_of_domain_action: 'This assistant only performs read-only troubleshooting.',
  },
  category: {
    software_bug: 'Software bug',
    hardware_fault: 'Hardware fault',
    firmware_issue: 'Firmware issue',
    config: 'Configuration',
    user_error: 'User error',
    network: 'Network',
    unknown: 'Unknown',
  },
  confidence: {
    high: 'High confidence',
    medium: 'Medium confidence',
    low: 'Low confidence',
  },
  status: {
    running: 'Running',
    completed: 'Completed',
    failed: 'Failed',
    refused: 'Declined',
  },
  handoff: {
    copy: 'Copy debug handoff',
    download: 'Download .md',
    preview: 'Preview debug handoff',
    copied: 'Debug handoff copied to clipboard.',
    copyFailed: 'Could not copy to clipboard.',
  },
  history: {
    title: 'Investigation history',
    refresh: 'Refresh',
    empty: 'No investigations yet.',
    loadFailed: 'Failed to load investigation history.',
    createdAt: 'Created',
    status: 'Status',
    anchor: 'Entity',
    category: 'Category',
    confidence: 'Confidence',
    model: 'Model',
  },
  detail: {
    title: 'Investigation detail',
    close: 'Close',
    created: 'Created',
    anchor: 'Entity',
    symptom: 'Symptom',
    steps: 'Steps',
    notFound: 'This investigation was not found or you do not have access to it.',
    loadFailed: 'Failed to load the investigation.',
  },
  // AI Fault Analytics page (sub-plan 19). Extends this shared namespace with its own subtree;
  // does not redefine menu.aiTroubleshooting (14-owned) or sibling subtrees (18/20).
  analytics: {
    title: 'AI Fault Analytics',
    subtitle: 'Investigation totals, trends over time, and recurring faults.',
    empty: 'No data for this period.',
    unknownModel: 'Unknown model',
    unknownSite: 'Unknown site',
    granularity: {
      label: 'Granularity',
      day: 'Day',
      week: 'Week',
    },
    cards: {
      totalInvestigations: 'Total investigations',
      refused: 'Refused',
      refusalRate: 'Refusal rate',
      handoffs: 'Handoffs',
      byCategory: 'By category',
      byStatus: 'By status',
    },
    charts: {
      investigationsOverTime: 'Investigations over time',
      recurringByCategory: 'Recurring faults by category',
      recurringByModel: 'Recurring faults by charger model',
      recurringBySite: 'Recurring faults by site',
    },
    table: {
      site: 'Site',
      model: 'Charger model',
      category: 'Category',
      count: 'Count',
    },
    category: {
      software_bug: 'Software bug',
      hardware_fault: 'Hardware fault',
      firmware_issue: 'Firmware issue',
      config: 'Configuration',
      user_error: 'User error',
      network: 'Network',
      unknown: 'Unknown',
      unclassified: 'Unclassified',
    },
    status: {
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
      refused: 'Refused',
    },
    errors: {
      loadFailed: 'Failed to load analytics',
    },
  },
  // Model A/B replay control (sub-plan 20). Extends this shared namespace with its own subtree;
  // does not redefine menu.aiTroubleshooting (14-owned) or sibling subtrees (18/19).
  replay: {
    title: 'Replay with model',
    subtitle:
      'Re-run the same symptom and anchor against a different model to compare results.',
    modelLabel: 'Model',
    run: 'Replay',
    running: 'Running replay...',
    loadingModels: 'Loading models...',
    noModels: 'No active investigation model configured.',
    success: 'Replay complete',
    errors: {
      investigationNotFound: 'Investigation not found or out of scope.',
      modelNotFound: 'Selected model is no longer available; pick another model.',
      modelUnavailable: 'The selected model is unavailable. Pick another model.',
      validation: 'Invalid request.',
      rateLimited: 'Rate limit reached. Try again shortly.',
      rateLimitedRetry: 'Rate limit reached. Try again in %{seconds}s.',
      generic: 'Replay failed.',
    },
  },
  // Model A/B side-by-side comparison (sub-plan 20).
  comparison: {
    title: 'Model comparison',
    notAvailable: 'N/A',
    original: 'Original',
    status: 'Status',
    category: 'Category',
    confidence: 'Confidence',
    conclusion: 'Conclusion',
    tokens: 'Tokens (in / out)',
    cost: 'Cost',
    latency: 'Latency',
    symptom: 'Symptom',
    anchor: 'Entity',
    singleRunHint: 'Run a replay with another model to compare.',
    noRuns: 'No runs to compare.',
    notFound: 'Comparison unavailable (investigation not found or out of scope).',
    loadFailed: 'Failed to load the comparison.',
  },
};
